import classNames from 'classnames/bind';
import React, { useEffect, useRef } from 'react';

import styles from './Listbox.module.scss';

/** Properties interface of the listbox. */
export interface ListboxProps {
  /**
   * Identifies the element(s) that label the listbox.
   * @see [aria-labelledby](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-labelledby)
   */
  ariaLabelledBy?: React.AriaAttributes['aria-labelledby'];

  /** Child elements of the listbox. */
  children: React.ReactNode;

  /** Custom classname of the listbox. */
  className?: string;

  /** Indicates that the listbox should be hidden. */
  hidden: boolean;

  /** Unique identifier of the listbox. */
  id: string;
}

/** Default properties of the listbox. */
const defaultProps: Partial<ListboxProps> = {
  hidden: true,
};

/**
 * Classnames binding for the listbox.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

/**
 * A listbox helper component.
 *
 * @see [W3C Listbox Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/listbox/)
 *
 * @param props - Listbox properties.
 * @returns A JSX element.
 */
const Listbox: React.FC<ListboxProps> = (props) => {
  const { ariaLabelledBy, children, className, hidden, id } = props;
  const listBoxRef = useRef<HTMLUListElement>(null);

  const classes = cx({ listbox: true }, className);

  /*If listbox is not hidden, and the bottom is off the page, scroll until the bottom
   of the listbox aligns with the bottom of the page, unless the listbox is larger than
   the viewing port, then align top of listbox to top of page */
  useEffect(() => {
    if (listBoxRef.current != null) {
      const listBottom = listBoxRef.current.getBoundingClientRect().bottom;
      const listHeight = listBoxRef.current.getBoundingClientRect().height;
      const viewportHeight = window.innerHeight;
      if (!hidden && listBottom > viewportHeight) {
        document
          .getElementById(id)
          ?.scrollIntoView({ behavior: 'smooth', block: listHeight < viewportHeight ? 'end' : 'start' });
      }
    }
  }, [id, hidden]);

  return (
    <ul
      aria-labelledby={ariaLabelledBy}
      className={classes}
      hidden={hidden}
      id={id}
      role="listbox"
      tabIndex={-1}
      ref={listBoxRef}
    >
      {children}
    </ul>
  );
};

Listbox.defaultProps = defaultProps;
Listbox.displayName = 'Listbox';

export default Listbox;
